/**
 * Reading the point cloud the engine actually produced.
 *
 * Written against a real artifact rather than the format in general. COLMAP's
 * `model_converter --output_type PLY` writes exactly this, verified byte for
 * byte against a production reconstruction of 23,473 points:
 *
 *     ply
 *     format binary_little_endian 1.0
 *     element vertex 23473
 *     property float x
 *     property float y
 *     property float z
 *     property uchar red
 *     property uchar green
 *     property uchar blue
 *     end_header
 *     <15 bytes per vertex>
 *
 * 23,473 x 15 = 352,095, and the file is 352,274 bytes: header plus payload,
 * with nothing else in it.
 *
 * It still parses the header instead of assuming those offsets, because a
 * future engine or profile could add normals and the failure mode of assuming
 * would be a cloud that renders - wrongly, silently, at coordinates taken from
 * the wrong columns. What it will not do is guess: an encoding it does not
 * understand throws, and the screen says so.
 *
 * Nothing here copies the file. The header is decoded from a small slice, and
 * the payload is read through a `DataView` over the original buffer into two
 * typed arrays sized exactly for what the renderer uploads.
 */

/** Byte width of each PLY scalar type, by every name the format uses. */
const SCALAR_BYTES: Record<string, number> = {
  char: 1, int8: 1,
  uchar: 1, uint8: 1,
  short: 2, int16: 2,
  ushort: 2, uint16: 2,
  int: 4, int32: 4,
  uint: 4, uint32: 4,
  float: 4, float32: 4,
  double: 8, float64: 8,
};

export interface PointCloud {
  /** How many points were read. Never inferred from the header alone. */
  count: number;
  /** `[x, y, z, ...]`, one triple per point. */
  positions: Float32Array;
  /** `[r, g, b, ...]` 0-255, or null when the file carried no colour. */
  colors: Uint8Array | null;
}

export class PlyParseError extends Error {}

interface Property {
  name: string;
  offset: number;
  type: string;
}

/** Reads one scalar, whatever width the header said it was. */
function readScalar(view: DataView, at: number, type: string): number {
  switch (type) {
    case "float":
    case "float32":
      return view.getFloat32(at, true);
    case "double":
    case "float64":
      return view.getFloat64(at, true);
    case "uchar":
    case "uint8":
      return view.getUint8(at);
    case "char":
    case "int8":
      return view.getInt8(at);
    case "ushort":
    case "uint16":
      return view.getUint16(at, true);
    case "short":
    case "int16":
      return view.getInt16(at, true);
    case "uint":
    case "uint32":
      return view.getUint32(at, true);
    case "int":
    case "int32":
      return view.getInt32(at, true);
    default:
      throw new PlyParseError(`desteklenmeyen veri tipi: ${type}`);
  }
}

export function parsePly(buffer: ArrayBuffer): PointCloud {
  const bytes = new Uint8Array(buffer);
  // The header is ASCII and short. Decoding only the first 64 KB keeps a large
  // binary payload out of the text decoder entirely.
  const probe = new TextDecoder("ascii").decode(bytes.subarray(0, Math.min(bytes.length, 65536)));
  const terminator = probe.indexOf("end_header");
  if (!probe.startsWith("ply") || terminator === -1) {
    throw new PlyParseError("Bu dosya bir PLY nokta bulutu değil.");
  }
  const newline = probe.indexOf("\n", terminator);
  if (newline === -1) throw new PlyParseError("PLY başlığı eksik.");
  const dataStart = newline + 1;

  const lines = probe.slice(0, terminator).split(/\r?\n/);
  const format = lines.find((line) => line.startsWith("format"));
  if (!format || !format.includes("binary_little_endian")) {
    // ASCII and big-endian PLYs exist; this engine writes neither, so
    // supporting them would be untested code guarding an impossible case.
    throw new PlyParseError("Yalnızca ikili (little-endian) PLY okunabilir.");
  }

  let count = 0;
  let stride = 0;
  const properties: Property[] = [];
  let inVertexElement = false;

  for (const line of lines) {
    const parts = line.trim().split(/\s+/);
    if (parts[0] === "element") {
      // Properties belong to the element that precedes them; a `face` element
      // after `vertex` must not contribute to the vertex stride.
      inVertexElement = parts[1] === "vertex";
      if (inVertexElement) count = Number(parts[2]);
      continue;
    }
    if (parts[0] !== "property" || !inVertexElement) continue;
    const [, type, name] = parts;
    if (type === "list") {
      throw new PlyParseError("Değişken uzunlukta özellik içeren PLY okunamıyor.");
    }
    if (!type || !name) throw new PlyParseError("PLY başlığı okunamadı.");
    const width = SCALAR_BYTES[type];
    if (!width) throw new PlyParseError(`desteklenmeyen veri tipi: ${type}`);
    properties.push({ name, offset: stride, type });
    stride += width;
  }

  if (!Number.isFinite(count) || count <= 0) {
    throw new PlyParseError("PLY dosyasında nokta yok.");
  }

  const find = (name: string) => properties.find((p) => p.name === name);
  const px = find("x");
  const py = find("y");
  const pz = find("z");
  if (!px || !py || !pz) throw new PlyParseError("PLY dosyasında konum verisi yok.");

  const red = find("red") ?? find("r");
  const green = find("green") ?? find("g");
  const blue = find("blue") ?? find("b");
  const hasColor = Boolean(red && green && blue);

  const available = bytes.length - dataStart;
  const readable = Math.min(count, Math.floor(available / stride));
  if (readable <= 0) throw new PlyParseError("PLY verisi eksik.");

  const view = new DataView(buffer, dataStart);
  const positions = new Float32Array(readable * 3);
  const colors = hasColor ? new Uint8Array(readable * 3) : null;

  for (let i = 0; i < readable; i += 1) {
    const base = i * stride;
    positions[i * 3] = readScalar(view, base + px.offset, px.type);
    positions[i * 3 + 1] = readScalar(view, base + py.offset, py.type);
    positions[i * 3 + 2] = readScalar(view, base + pz.offset, pz.type);
    if (colors && red && green && blue) {
      colors[i * 3] = readScalar(view, base + red.offset, red.type);
      colors[i * 3 + 1] = readScalar(view, base + green.offset, green.type);
      colors[i * 3 + 2] = readScalar(view, base + blue.offset, blue.type);
    }
  }

  // `readable`, not the header's `count`. A truncated file reports what it
  // actually contained rather than what it claimed to.
  return { count: readable, positions, colors };
}

/**
 * Centre and radius of the cloud, for framing the camera.
 *
 * The median-ish centre of the bounding box rather than the mean: a sparse
 * solve routinely has a handful of far-flung outliers, and averaging lets them
 * drag the camera somewhere the actual structure is a speck.
 */
export function boundsOf(positions: Float32Array): {
  center: [number, number, number];
  radius: number;
} {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i + 2 < positions.length; i += 3) {
    // `Number(...)` rather than an assertion: an out-of-range read becomes NaN
    // and is skipped by the finite check below, which a solve genuinely needs -
    // COLMAP can emit non-finite coordinates for a badly constrained point.
    const x = Number(positions[i]);
    const y = Number(positions[i + 1]);
    const z = Number(positions[i + 2]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  if (!Number.isFinite(minX)) return { center: [0, 0, 0], radius: 1 };
  const center: [number, number, number] = [
    (minX + maxX) / 2,
    (minY + maxY) / 2,
    (minZ + maxZ) / 2,
  ];
  const radius = Math.max(maxX - minX, maxY - minY, maxZ - minZ) / 2 || 1;
  return { center, radius };
}
